import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import type { BillingInterval, Quality } from "@sp/shared/enums";
import { useSession } from "../../app/session";
import { useWorkspace } from "../../app/workspace";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/Dialog";
import { ErrorState } from "../../components/ui/ErrorState";
import { Field, Input } from "../../components/ui/Field";
import { Loading, Note, PageHeader } from "../../components/ui/Page";
import { Pill } from "../../components/ui/Pill";
import { useAction } from "../../hooks/useAction";
import { usePoll } from "../../hooks/usePoll";
import { useResource } from "../../hooks/useResource";
import { billingApi } from "../../lib/api/billing";
import type { Billing, Plan, Price } from "../../lib/api/types";
import {
  billedEvery, hasLiveSubscription, INTERVAL_LABEL, INTERVAL_MONTHS, intervalOffer, intervalsOffered, isCurrentPrice, monthsFree, perMonthMinor, priceFor, savingPercent, STATUS_TEXT,
} from "../../lib/billing";
import { formatBytes, formatDate, formatMoney, plural } from "../../lib/format";
import { cx } from "../../components/ui/cx";

function CurrentPlan({ b, onPortal, portal }: { b: Billing; onPortal: () => void; portal: { pending: boolean; error: string | null } }) {
  const sub = b.subscription;
  return (
    <Card raised>
      <CardHeader
        title={b.plan?.name ?? "No plan"}
        caption="current plan"
        actions={b.canManage && sub?.hasBillingAccount && b.billingEnabled && (
          <Button size="sm" pending={portal.pending} onClick={onPortal}>invoices, card and cancel ↗</Button>
        )}
      />
      <div className="flex flex-col gap-3 px-5 py-4">
        {sub ? (
          <p className="flex flex-wrap items-center gap-3">
            <Pill tone={sub.status === "active" || sub.status === "trialing" ? "ok" : "bad"}>{STATUS_TEXT[sub.status] ?? sub.status}</Pill>
            {sub.price && <span>{formatMoney(sub.price.amountMinor, sub.price.currency)} {billedEvery(sub.price.interval)}</span>}
            <span className="text-muted">
              {sub.status === "expired" ? `ended ${formatDate(sub.expiredAt)}`
                : sub.cancelAtPeriodEnd ? `ends ${formatDate(sub.currentPeriodEnd)}`
                : sub.status === "trialing" ? `trial ends ${formatDate(sub.currentPeriodEnd)}`
                : `renews ${formatDate(sub.currentPeriodEnd)}`}
            </span>
          </p>
        ) : <p className="text-muted">This workspace has never had a plan.</p>}
        {sub?.status === "past_due" && (
          <Banner tone="bad">The last payment didn't go through. Update your card under "invoices, card and cancel" to keep streaming.</Banner>
        )}
        {sub?.retentionDeleteAt && (
          <Banner tone="warn">Videos in this workspace will be deleted on {formatDate(sub.retentionDeleteAt)} unless you choose a plan.</Banner>
        )}
        {sub?.planChange?.status === "pending" && <Banner tone="info">Your plan change is being processed…</Banner>}
        {sub?.planChange?.status === "failed" && (
          <Banner tone="bad">The plan change didn't go through{sub.planChange.error ? `: ${sub.planChange.error}` : "."}</Banner>
        )}
        {portal.error && <Banner tone="bad">{portal.error}</Banner>}
      </div>
    </Card>
  );
}

function PlanCard({ plan, price, current, canBuy, onChoose }: {
  plan: Plan; price: Price | undefined; current: boolean; canBuy: boolean; onChoose: (price: Price) => void;
}) {
  const l = plan.limits;
  const saving = price ? savingPercent(plan, price) : 0;
  const free = price ? monthsFree(plan, price) : 0;
  const offer = free > 0 ? `${plural(free, "month")} free` : saving > 0 ? `save ${saving}%` : null;
  const monthly = priceFor(plan, "month");
  return (
    <Card as="article" raised={current} className={cx("flex flex-col", current && "bg-accent-tint")}>
      <div className="flex flex-col gap-1 border-b-2 border-ink px-5 py-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-2xl font-bold whitespace-nowrap">{plan.name}</h3>
          {/* A sticker in the neobrutalist style, so the yearly saving reads as an offer, not a footnote. */}
          {offer && price && price.interval !== "month" && (
            <span className="shrink-0 rotate-3 rounded-full border-2 border-ink bg-accent px-2 py-0.5 text-xs font-bold whitespace-nowrap text-white uppercase shadow-hard-sm motion-safe:animate-rise">
              {offer}
            </span>
          )}
        </div>
        <span className="flex flex-wrap items-baseline gap-x-2 font-display text-3xl font-bold">
          {offer && monthly && price && price.interval !== "month" && (
            <s className="text-xl font-semibold text-faint decoration-accent decoration-2" aria-label={`was ${formatMoney(monthly.amountMinor, monthly.currency)}`}>
              {formatMoney(monthly.amountMinor, monthly.currency)}
            </s>
          )}
          <span>
            {price ? formatMoney(perMonthMinor(price), price.currency) : "-"}
            {price && <span className="text-base font-medium text-muted"> / month</span>}
          </span>
        </span>
        {price && price.interval !== "month" && (
          <span className="text-muted">
            {formatMoney(price.amountMinor, price.currency)} {billedEvery(price.interval)}
            {offer && monthly && <> instead of {formatMoney(monthly.amountMinor * INTERVAL_MONTHS[price.interval], monthly.currency)}</>}
          </span>
        )}
      </div>
      <ul className="flex flex-1 flex-col gap-1.5 px-5 py-4">
        <li>{plural(l.maxConcurrentStreams, "stream")} at once</li>
        <li>up to {l.maxVideoQuality === "4k" ? "4K" : "HD"} at {l.maxFps} fps</li>
        <li>{formatBytes(l.storageBytes)} storage{l.maxFiles === null ? "" : `, ${l.maxFiles} files`}</li>
        <li>files up to {formatBytes(l.maxFileBytes)}</li>
        <li>{plural(l.maxMembers, "member")}</li>
        <li>{l.analyticsRetentionDays} days of history</li>
      </ul>
      <div className="px-5 pb-5">
        {current ? <Pill tone="ok">your plan</Pill> : (
          <Button variant="primary" className="w-full" disabled={!canBuy || !price?.purchasable} onClick={() => price && onChoose(price)}>
            {price && !price.purchasable ? "not on sale yet" : `choose ${plan.name.toLowerCase()} →`}
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * The more-streams plans as one compact table under the plan cards, HD or 4K at a time, so ten plans don't become ten cards.
 * Worded as whole plans, not add-ons: choosing one replaces the current plan.
 */
function MoreStreams({ plans, currentPlanId, hasPlan, interval, isCurrent, canBuy, onChoose }: {
  plans: Plan[]; currentPlanId: string | undefined; hasPlan: boolean; interval: BillingInterval; isCurrent: (price: Price | undefined) => boolean; canBuy: boolean;
  onChoose: (price: Price) => void;
}) {
  // Open on the quality of the plan they're on, so their own row is in view.
  const [quality, setQuality] = useState<Quality>(plans.find((p) => p.id === currentPlanId)?.limits.maxVideoQuality ?? "hd");
  const qualities = (["hd", "4k"] as const).filter((q) => plans.some((p) => p.limits.maxVideoQuality === q));
  const shown = plans.filter((p) => p.limits.maxVideoQuality === quality);
  const first = plans[0]?.limits;
  return (
    <Card as="section" aria-labelledby="more-streams" className="flex flex-col">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-ink px-5 py-4">
        <div className="flex flex-col gap-1">
          <h3 id="more-streams" className="text-2xl font-bold">Plans for 10 to 20 streams</h3>
          {first && (
            <span className="text-muted">
              Full plans for larger channels, with {plural(first.maxMembers, "member")} and {first.analyticsRetentionDays} days of history.
              {hasPlan && " Choosing one replaces your current plan."}
            </span>
          )}
        </div>
        {qualities.length > 1 && (
          <fieldset className="flex gap-1 rounded-full border-2 border-ink bg-card p-1">
            <legend className="sr-only">quality</legend>
            {qualities.map((q) => (
              <label key={q} className={cx("flex min-h-9 cursor-pointer items-center rounded-full px-4 font-medium has-focus-visible:outline-3 has-focus-visible:outline-accent", quality === q ? "bg-ink text-white" : "bg-card text-muted hover:text-ink")}>
                <input type="radio" name="more-streams-quality" value={q} checked={quality === q} onChange={() => setQuality(q)} className="sr-only" />
                {q === "4k" ? "4K" : "HD"}
              </label>
            ))}
          </fieldset>
        )}
      </div>
      <ul className="flex flex-col">
        {shown.map((p) => {
          const price = priceFor(p, interval);
          const current = isCurrent(price);
          const l = p.limits;
          return (
            <li key={p.id} className={cx("flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-line px-5 py-3 last:border-b-0", current && "bg-accent-tint")}>
              <span className="flex min-w-36 flex-1 flex-col">
                <span className="text-lg font-bold">{p.name}</span>
                <span className="text-sm text-muted">{plural(l.maxConcurrentStreams, "stream")} at {l.maxVideoQuality === "4k" ? "4K" : "HD"} {l.maxFps} fps · {formatBytes(l.storageBytes)}</span>
              </span>
              <span className="flex min-w-36 flex-col">
                <span className="font-display text-xl font-bold">
                  {price ? formatMoney(perMonthMinor(price), price.currency) : "-"}
                  {price && <span className="text-sm font-medium text-muted"> / month</span>}
                </span>
                {price && (
                  <span className="text-sm text-muted">
                    {formatMoney(Math.round(perMonthMinor(price) / l.maxConcurrentStreams), price.currency)} per stream
                    {price.interval !== "month" && <>, {formatMoney(price.amountMinor, price.currency)} {billedEvery(price.interval)}</>}
                  </span>
                )}
              </span>
              <span className="w-full sm:w-40">
                {current ? <Pill tone="ok">your plan</Pill> : (
                  <Button variant="primary" size="sm" className="w-full" disabled={!canBuy || !price?.purchasable} onClick={() => price && onChoose(price)}>
                    {price && !price.purchasable ? "not on sale yet" : `choose ${p.name} →`}
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function BillingPage() {
  const { workspace } = useWorkspace();
  const { refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const returned = params.has("workspace"); // Dodo sends people back here with ?workspace=
  const plans = useResource((signal) => billingApi.plans(signal), []);
  const billing = useResource((signal) => billingApi.state(workspace.id, signal), [workspace.id]);
  const [interval, setBillingInterval] = useState<BillingInterval>("month");
  const [promo, setPromo] = useState("");
  const [changeTo, setChangeTo] = useState<Price | null>(null);

  const b = billing.data;
  // Stop waiting for the webhook after two minutes (e.g. the checkout was abandoned).
  useEffect(() => {
    if (!returned) return;
    const t = setTimeout(() => setParams({}, { replace: true }), 120_000);
    return () => clearTimeout(t);
  }, [returned, setParams]);
  const pending = b?.subscription?.planChange?.status === "pending";
  // After checkout the webhook brings the new state; keep checking briefly so it shows up without a reload.
  usePoll(async () => {
    await billing.reload();
    if (returned && b?.subscription?.status === "active") { setParams({}, { replace: true }); void refresh(); }
  }, 3_000, pending || returned);

  const checkout = useAction(async (price: Price) => {
    location.assign(await billingApi.checkout(workspace.id, price.id, promo.trim() || undefined));
  });
  const portal = useAction(async () => { location.assign(await billingApi.portal(workspace.id)); });
  const change = useAction(async (price: Price) => {
    await billingApi.changePlan(workspace.id, price.id);
    setChangeTo(null);
    await billing.reload();
  });

  if (billing.error !== undefined) return <ErrorState error={billing.error} onRetry={() => void billing.reload()} />;
  if (!b) return <Loading />;

  const live = hasLiveSubscription(b);
  const offered = plans.data ? intervalsOffered(plans.data.plans) : [];
  const paid = plans.data?.plans.filter((p) => p.kind === "paid") ?? [];
  const primary = paid.filter((p) => p.displayGroup === "primary");
  const moreStreams = paid.filter((p) => p.displayGroup === "more_streams");
  const choose = (pr: Price) => (live ? setChangeTo(pr) : void checkout.run(pr));
  const canBuy = b.canManage && b.billingEnabled && !pending;

  return (
    <>
      <PageHeader caption={`workspace: ${workspace.name.toLowerCase()}`} title="Billing" />
      {returned && b.subscription?.status !== "active" && <Banner tone="info">Confirming your payment. This page updates on its own.</Banner>}
      {!b.billingEnabled && <Banner tone="warn">Payments aren't set up on this server yet, so plans can't be bought.</Banner>}
      {!b.canManage && <Banner tone="info">Only the workspace owner or an admin can change the plan.</Banner>}
      <CurrentPlan b={b} onPortal={() => void portal.run()} portal={portal} />

      {plans.error !== undefined && <ErrorState error={plans.error} onRetry={() => void plans.reload()} />}
      {plans.data && (
        <section aria-label="Plans" className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="text-2xl font-bold">Plans</h2>
            {offered.length > 1 && (
              <fieldset className="flex flex-wrap gap-1 rounded-full border-2 border-ink bg-card p-1 shadow-hard-sm">
                <legend className="sr-only">billing period</legend>
                {offered.map((i) => (
                  <label key={i} className={cx("flex min-h-9 cursor-pointer items-center rounded-full px-4 font-medium motion-safe:transition-colors motion-safe:duration-300 has-focus-visible:outline-3 has-focus-visible:outline-accent", interval === i ? "bg-ink text-white" : "bg-card text-muted hover:text-ink")}>
                    <input type="radio" name="interval" value={i} checked={interval === i} onChange={() => setBillingInterval(i)} className="sr-only" />
                    {INTERVAL_LABEL[i]}
                    {intervalOffer(paid, i) && (
                      <span className={cx("ml-2 rounded-full px-2 py-0.5 font-mono text-[11px] font-bold uppercase", interval === i ? "bg-accent text-white" : "bg-accent-tint text-accent-deep")}>
                        {intervalOffer(paid, i)}
                      </span>
                    )}
                  </label>
                ))}
              </fieldset>
            )}
          </div>
          {plans.data.promotion?.badgeText && <Banner tone="ok">{plans.data.promotion.badgeText}</Banner>}
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {primary.map((p) => {
              const price = priceFor(p, interval);
              return <PlanCard key={p.id} plan={p} price={price} current={isCurrentPrice(b, price)} canBuy={canBuy} onChoose={choose} />;
            })}
          </div>
          {moreStreams.length > 0 && (
            <MoreStreams plans={moreStreams} currentPlanId={b.plan?.id} hasPlan={!!b.plan} interval={interval} isCurrent={(price) => isCurrentPrice(b, price)} canBuy={canBuy} onChoose={choose} />
          )}
          {!live && canBuy && (
            <Field label="promo code (optional)" className="max-w-xs">
              {({ id }) => <Input id={id} value={promo} maxLength={64} onChange={(e) => setPromo(e.target.value)} placeholder="e.g. LAUNCH20" autoComplete="off" />}
            </Field>
          )}
          {checkout.error && <Banner tone="bad">{checkout.error}</Banner>}
          <Note>payments, invoices and VAT are handled by Dodo Payments. you're sent to their page and back.</Note>
        </section>
      )}

      <ConfirmDialog
        open={!!changeTo}
        title="Change plan?"
        confirmLabel="change plan"
        pending={change.pending}
        onConfirm={() => changeTo && void change.run(changeTo)}
        onCancel={() => { setChangeTo(null); change.clearError(); }}
      >
        <p>The change applies right away. You're charged or credited the difference for the rest of this period.</p>
        {change.error && <Banner tone="bad">{change.error}</Banner>}
      </ConfirmDialog>
    </>
  );
}
